import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import { SelectItemsProps } from "../../components/Select2Component";
import SelectComponent from "../../components/SelectComponent";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import RoutesObject from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { Parameter } from "../../models/Parameter";
import { UserListDTO } from "../../models/User/UserListDTO";
import { AdminService } from "../../services/AdminService";
import "../../styles/page.css";

export default function UserIndex() {
    const intl = useIntl();
    //Declaração de outros estados e variáveis importantes
    const navigate = useNavigate();
    const adminService = new AdminService();
    const pageSizes: number[] = [5, 15, 30];
    const [data, setData] = useState<UserListDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [searchParams] = useSearchParams();

    //Vamos tentar obter a page e a pageSize pelos query parameters
    //Usamos useState em vez de simple var, para não estar sempre a obter o valor a cada re-render, desta forma o page e o pageSize são sempre o mesmo
    //porque nunca estamos a fazer setState à var
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes));

    //Search Parameters
    const [name, setName] = useState<string>(
        Utilities.LoadParameterFromURLQuery("name", "string", ""),
    );
    const [email, setEmail] = useState<string>(
        Utilities.LoadParameterFromURLQuery("email", "string", ""),
    );
    const [active, setActive] = useState<string>(
        Utilities.LoadParameterFromURLQuery("active", "string", "True"),
    );
    const [role, setRole] = useState<string>(
        Utilities.LoadParameterFromURLQuery("role", "string", ""),
    );
    const [subcontractor, setSubcontractor] = useState<string>(
        Utilities.LoadParameterFromURLQuery("subcontractor", "string", ""),
    );
    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [goToPage, setGoToPage] = useState<number>();
    const [initialSortBy] = useState<SortingRule<object>[]>(
        Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"),
    );
    const [rolesList, setRolesList] = useState<SelectItemsProps[]>([]);
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    const [isActiveOutsourcingModule, setIsActiveOutsourcingModule] = useState<boolean>(false);
    const { availableModules, assistanceTypes } = useAvailableModulesContext();

    useEffect(() => {
        fetchRoles();
        if (availableModules.includes("IsActiveOutsourcingModule")) {
            setIsActiveOutsourcingModule(true);
        }
    }, [availableModules]);

    const columns = React.useMemo(
        () => [
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) => OperationsCell(props.cell.row.original.id, "user"),
                width: 5
            },
            {
                Header: <FormattedMessage id="Screens.Users.Table.Column1" />,
                accessor: "name",
                width: isActiveOutsourcingModule ? 20 : 25
            },
            {
                Header: <FormattedMessage id="Screens.Users.Table.Column2" />,
                accessor: "email",
                width: isActiveOutsourcingModule ? 25 : 35
            },
            {
                Header: <FormattedMessage id="Screens.Users.Table.Column3" />,
                accessor: "roles",
                Cell: (props: any) => {
                    return (
                        <>
                            {props.cell.row.original.roles.map((role: string) => (
                                <div key={role}>{role === "Técnico Oficina" ? "Técnico" : role}</div>
                            ))}
                        </>
                    );
                },
                width: isActiveOutsourcingModule ? 10 : 15
            },
            {
                Header: <FormattedMessage id="Screens.Users.Table.Column4" />,
                accessor: "active",
                Cell: (props: any) => {
                    return (
                        <div>
                            <span
                                style={{
                                    color: props.cell.row.original.active ? "#6DD400" : "#C75428",
                                }}
                            >
                                {props.cell.row.original.active ? (
                                    <FormattedMessage id="Screens.Users.Table.Active" />
                                ) : (
                                    <FormattedMessage id="Screens.Users.Table.Inactive" />
                                )}
                            </span>
                        </div>
                    );
                },
                width: isActiveOutsourcingModule ? 10 : 20
            },
            ...(isActiveOutsourcingModule
                ? [
                    {
                        Header: <FormattedMessage id="Screens.Users.Table.Subcontractor" />,
                        accessor: "subcontractorCompanyName",
                        width: 30
                    },
                ] : []),
        ],
        [isActiveOutsourcingModule],
    );

    const statusList: SelectItemsProps[] = [
        {
            label: intl.formatMessage({ id: "Screens.Users.Table.Active" }),
            value: "True",
        },
        {
            label: intl.formatMessage({ id: "Screens.Users.Table.Inactive" }),
            value: "False",
        },
    ];

    const fetchData = async (
        page: number,
        pageSize: number,
        sortBy: SortingRule<object>[] | null,
    ) => {
        //Atualizamos na history do browser a current page e pageSize

        var searchUrl: string =
            `?page=${page}&pageSize=${pageSize}${Utilities.AddParamToQueryIfValue(
                "name",
                "string",
                name,
            )}${Utilities.AddParamToQueryIfValue(
                "email",
                "string",
                email,
            )}${Utilities.AddParamToQueryIfValue("active", "string", active)}` +
            `${Utilities.AddParamToQueryIfValue("role", "string", role)}` +
            `${Utilities.AddParamToQueryIfValue("subcontractor", "string", subcontractor)}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id,
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC",
            )}`;
        }

        //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota

        if (searchParams.toString() === "") {
            navigate(`/users${searchUrl}`, { replace: true });
        } else {
            //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
            navigate(`/users${searchUrl}`);
        }

        setLoading(true);

        //Vamos obter os dados
        var response = await adminService.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize,
        );

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(0);
            setCurrentPage(0);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        //Atribuímos os dados
        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);

        //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    //Método para montar os parâmetros de pesquisa
    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];

        parameters.push(new Parameter("name", name));
        parameters.push(new Parameter("email", email));
        parameters.push(new Parameter("active", active));
        parameters.push(new Parameter("role", role));
        parameters.push(new Parameter("subcontractor", subcontractor));

        return parameters;
    };

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };

    const onButtonClick = async () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };

    const fetchRoles = async () => {
        var responseGetRoles = await adminService.GetRoles();
        if (responseGetRoles.success === false) {
            Toast.Show("error", responseGetRoles.message);
            return;
        }

        var items: SelectItemsProps[] = [];
        responseGetRoles.obj.forEach((s) => {
            items.push({
                label: s.name,
                value: s.name,
            });
        });

        setRolesList(items);
    };

    const onRemoveFiltersClick = async () => {
        setName("");
        setActive("");
        setEmail("");
        setRole("");
        setSubcontractor("");
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);

    const onNewTaskClick = () => {
        navigate(RoutesObject.users.create);
    };

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <span className="pageTitle">
                        <FormattedMessage id="Screens.Users.Title" />
                    </span>
                </div>

                <Row>
                    <Col xl={2} lg={3} md={3} sm={12} xs={12} style={{ marginTop: "1em" }}>
                        <ButtonComponent
                            text={intl.formatMessage({ id: "Screens.User.Button.CreateUser" })}
                            onClick={onNewTaskClick} />
                    </Col>
                </Row>

                <div className="pageBody">
                    <Row>
                        <InputComponent
                            xl={2}
                            lg={3}
                            md={5}
                            sm={12}
                            xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            onEnter={onEnter}
                            value={name}
                            label={intl.formatMessage({ id: "Screens.Users.SearchField1" })}
                            readOnly={false}
                            onChange={(t: string) => setName(t)}
                        />
                        <InputComponent
                            xl={2}
                            lg={3}
                            md={5}
                            sm={12}
                            xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            onEnter={onEnter}
                            value={email}
                            label={intl.formatMessage({ id: "Screens.Users.SearchField2" })}
                            readOnly={false}
                            onChange={(t: string) => setEmail(t)}
                        />
                        <SelectComponent
                            xl={2}
                            lg={3}
                            md={5}
                            sm={12}
                            xs={12}
                            style={{ width: "100%" }}
                            label={intl.formatMessage({ id: "Screens.Users.SearchField4" })}
                            selectedItemValue={role}
                            items={rolesList}
                            onChange={(t: string) => {
                                setRole(t);
                            }}
                        />
                        <SelectComponent
                            xl={2}
                            lg={3}
                            md={5}
                            sm={12}
                            xs={12}
                            style={{ width: "100%" }}
                            label={intl.formatMessage({ id: "Screens.Users.SearchField3" })}
                            selectedItemValue={active}
                            items={statusList}
                            onChange={(t: string) => {
                                setActive(t);
                            }}
                        />
                        {isActiveOutsourcingModule && (
                            <>
                                <InputComponent
                                    xl={2}
                                    lg={3}
                                    md={5}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type={"text"}
                                    onEnter={onEnter}
                                    value={subcontractor}
                                    label={intl.formatMessage({ id: "Screens.Users.SearchField.Subcontractor" })}
                                    readOnly={false}
                                    onChange={(t: string) => setSubcontractor(t)}
                                />
                            </>)}
                    </Row>

                    <Row>
                        <Col
                            xl={3}
                            lg={3}
                            md={3}
                            sm={12}
                            xs={12}
                            style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                        >
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                onClick={onButtonClick}
                            />
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                onClick={onRemoveFiltersClick}
                            />
                        </Col>
                    </Row>

                    <Row >
                        <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                            <div className="pageComponent" style={{ width: "100%" }}>
                                <TableComponent
                                    goToPage={goToPage}
                                    loading={loading}
                                    pageCount={pageCount}
                                    columns={columns}
                                    manualPagination={true}
                                    fetchData={fetchData}
                                    pageSizes={pageSizes}
                                    initialPage={page}
                                    initialPageSize={pageSize}
                                    initialSortBy={initialSortBy}
                                    data={data}
                                    dontOrderFirstCollumn={true}
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        </Layout>
    );
}
